import React, { useState, useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap, useMapEvents, Circle, GeoJSON, LayersControl, LayerGroup } from 'react-leaflet';
import MarkerClusterGroup from 'react-leaflet-cluster';
import L, { Icon, DivIcon } from 'leaflet';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Chip,
  Fab,
  Tooltip,
  useTheme,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField
} from '@mui/material';
import { MyLocation, Info, Speed, ShowChart, FilterAlt, Clear } from '@mui/icons-material';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as ChartTooltip, ResponsiveContainer } from 'recharts';
import { mapService, stationTimeseriesService, measurementsService, RadiusFilter } from '../services/api';
import { SUPPORTED_METRICS } from '../config/sensorMetrics';
import 'leaflet/dist/leaflet.css';

// Custom styles for transparent popup
const popupStyles = `
  .leaflet-popup-content-wrapper,
  .leaflet-popup-tip {
    background: transparent !important;
    box-shadow: none !important;
  }
`;

// Fix for default markers in react-leaflet
delete (Icon.Default.prototype as any)._getIconUrl;
Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

interface Location {
  latitude: number;
  longitude: number;
}

interface Station {
  id?: number | string;  // Database ID for public stations, or UUID string
  uuid?: string;  // UUID for owned stations
  name: string;
  description?: string;
  location: Location;
  elevation?: number;
  status: string;
  debug: boolean;
  latest_readings?: Record<string, any>;
  owner?: string;
  public: boolean;
  is_owner?: boolean;  // Whether current user owns this station
}

// Use centralized metric configuration
const metricConfig: Record<string, { name: string; color: string; unit: string }> = (() => {
  const config: Record<string, { name: string; color: string; unit: string }> = {};
  SUPPORTED_METRICS.forEach(metric => {
    config[metric.internal] = {
      name: metric.userFriendly.charAt(0).toUpperCase() + metric.userFriendly.slice(1).replace(/_/g, ' '),
      color: metric.color,
      unit: metric.unit
    };
  });
  return config;
})();

// Station Popup Component with charts
const StationPopupContent: React.FC<{ station: Station; isDarkMode: boolean; onViewDetails: () => void; onShowTimeSeries?: () => void }> = React.memo(({
  station,
  isDarkMode,
  onViewDetails,
  onShowTimeSeries
}) => {
  // Single batched state to avoid multiple re-renders / popup flicker
  const [data, setData] = useState<{
    loading: boolean;
    timeSeriesData: any[];
    latestReadings: Record<string, any> | null;
    stationInfo: { name: string; description: string };
  }>({
    loading: false,
    timeSeriesData: [],
    latestReadings: station.latest_readings || null,
    stationInfo: { name: station.name, description: '' },
  });
  const [showAllReadings, setShowAllReadings] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const fetchStationData = async () => {
      setData(prev => ({ ...prev, loading: true }));
      try {
        const stationIdentifier = (station.uuid || station.id) as string;

        const [tsData, latestData] = await Promise.all([
          stationTimeseriesService.getTimeseries(stationIdentifier, 24).catch(() => ({ timeseries: [] })),
          measurementsService.getLatest(stationIdentifier).catch(() => null),
        ]);

        if (cancelled) return;

        // Prepare all data before a single state update
        const formattedData = tsData.timeseries.map((point: any) => ({
          ...point,
          timestampMs: new Date(point.timestamp).getTime()
        }));

        let readings: Record<string, any> | null = data.latestReadings;
        let info = { name: station.name, description: '' };

        if (latestData) {
          info = { name: latestData.station_name || station.name, description: latestData.description || '' };
          if (latestData.measurements && latestData.measurements.length > 0) {
            const latest = latestData.measurements[0];
            const parsed: Record<string, any> = {};
            for (const [key, value] of Object.entries(latest)) {
              if (key !== 'timestamp' && value !== null && value !== undefined) {
                const metric = metricConfig[key];
                if (metric) {
                  parsed[key] = { value, unit: metric.unit };
                }
              }
            }
            readings = parsed;
          }
        }

        // Single setState — one re-render instead of 4
        setData({
          loading: false,
          timeSeriesData: formattedData,
          latestReadings: readings,
          stationInfo: info,
        });
      } catch (error) {
        console.error('Error fetching station data:', error);
        if (!cancelled) {
          setData(prev => ({ ...prev, loading: false }));
        }
      }
    };
    fetchStationData();
    return () => { cancelled = true; };
  }, [station.uuid || station.id]);

  const { loading, timeSeriesData, latestReadings, stationInfo } = data;

  const getAvailableMetrics = () => {
    if (timeSeriesData.length === 0) return [];
    const firstPoint = timeSeriesData[0];
    return Object.keys(firstPoint).filter(
      key => key !== 'timestamp' && key !== 'time' && key !== 'timestampMs' && firstPoint[key] !== null && firstPoint[key] !== undefined
    );
  };

  const hasCharts = !loading && timeSeriesData.length > 0;
  const hasReadings = latestReadings && Object.keys(latestReadings).length > 0;
  const [tabValue, setTabValue] = useState(0);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  return (
    <Card elevation={0} sx={{ backgroundColor: 'transparent' }}>
      <CardContent sx={{
        p: 2,
        '&:last-child': { pb: 2 },
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        backdropFilter: 'blur(10px)',
        borderRadius: 2,
        ...(isDarkMode && {
          backgroundColor: 'rgba(30, 30, 30, 0.95)',
          color: 'white'
        })
      }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <Typography variant="h6">
            {stationInfo.name}
          </Typography>
          <Chip
            label={station.status}
            size="small"
            color={station.status === 'Online' ? 'success' : station.status === 'Maintenance' ? 'warning' : 'error'}
          />
          {station.debug && (
            <Chip label="Debug" size="small" color="info" />
          )}
        </Box>
        {stationInfo.description && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontSize: '0.8rem' }}>
            {stationInfo.description}
          </Typography>
        )}

        {/* Tabs — always render all 3 to prevent layout shifts */}
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          variant="fullWidth"
          sx={{
            minHeight: 36,
            mb: 1,
            '& .MuiTab-root': {
              minHeight: 36,
              py: 0.5,
              fontSize: '0.75rem'
            }
          }}
        >
          <Tab icon={<Info sx={{ fontSize: 16 }} />} iconPosition="start" label="Info" />
          <Tab icon={<Speed sx={{ fontSize: 16 }} />} iconPosition="start" label="Readings" disabled={!hasReadings} />
          <Tab icon={<ShowChart sx={{ fontSize: 16 }} />} iconPosition="start" label="Trends" disabled={!hasCharts} />
        </Tabs>

        {/* Tab Content — fixed height to prevent popup resize flicker */}
        <Box sx={{ minHeight: 140, maxHeight: 220, overflow: 'auto' }}>
          {/* Info Tab */}
          {tabValue === 0 && (
            <Box>
              {station.description && (
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {station.description}
                </Typography>
              )}
              {station.owner && (
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  <strong>Provider:</strong> {station.owner}
                </Typography>
              )}
              {!station.description && !station.owner && (
                <Typography variant="body2" color="text.secondary">
                  No additional information available.
                </Typography>
              )}
            </Box>
          )}

          {/* Readings Tab */}
          {hasReadings && tabValue === 1 && (() => {
            const entries = Object.entries(latestReadings!);
            const visible = showAllReadings ? entries : entries.slice(0, 4);
            return (
              <Box>
                {visible.map(([sensor, reading]) => (
                  <Typography key={sensor} variant="body2" sx={{ fontSize: '0.8rem', lineHeight: 1.6 }}>
                    <strong>{metricConfig[sensor]?.name || sensor}:</strong>{' '}
                    {typeof reading.value === 'number' ? reading.value.toFixed(1) : reading.value} {reading.unit}
                  </Typography>
                ))}
                {entries.length > 4 && (
                  <Typography
                    variant="caption"
                    sx={{ cursor: 'pointer', color: 'primary.main', display: 'block', mt: 0.5, '&:hover': { textDecoration: 'underline' } }}
                    onClick={(e) => { e.stopPropagation(); setShowAllReadings(!showAllReadings); }}
                  >
                    {showAllReadings ? 'Show less' : `Show ${entries.length - 4} more...`}
                  </Typography>
                )}
              </Box>
            );
          })()}

          {/* Trends Tab */}
          {hasCharts && tabValue === 2 && (
            <Box>
              {getAvailableMetrics().slice(0, 2).map((metric) => {
                const config = metricConfig[metric];
                if (!config) return null;

                return (
                  <Box key={metric} sx={{ mb: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      {config.name}
                    </Typography>
                    <ResponsiveContainer width="100%" height={80}>
                      <LineChart data={timeSeriesData}>
                        <CartesianGrid strokeDasharray="3 3" stroke={isDarkMode ? '#444' : '#ddd'} />
                        <XAxis
                          dataKey="timestampMs"
                          type="number"
                          scale="time"
                          domain={['dataMin', 'dataMax']}
                          tick={{ fontSize: 8, fill: isDarkMode ? '#aaa' : '#666' }}
                          tickFormatter={(ts) => new Date(ts).toLocaleTimeString('en-US', {
                            hour: '2-digit',
                            minute: '2-digit',
                            second: '2-digit'
                          })}
                        />
                        <YAxis
                          tick={{ fontSize: 8, fill: isDarkMode ? '#aaa' : '#666' }}
                          width={30}
                        />
                        <ChartTooltip
                          contentStyle={{
                            backgroundColor: '#333333',
                            border: '1px solid #555555',
                            borderRadius: '4px',
                            color: '#ffffff'
                          }}
                          labelStyle={{ color: '#ffffff' }}
                          itemStyle={{ color: '#ffffff' }}
                          labelFormatter={(ts) => new Date(ts).toLocaleString('en-GB', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                            second: '2-digit'
                          })}
                        />
                        <Line
                          type="monotone"
                          dataKey={metric}
                          stroke={config.color}
                          strokeWidth={2}
                          dot={false}
                          name={`${config.name} (${config.unit})`}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>

        {onShowTimeSeries && (
          <Button
            size="small"
            variant="outlined"
            startIcon={<ShowChart sx={{ fontSize: 16 }} />}
            onClick={(e) => { e.stopPropagation(); onShowTimeSeries(); }}
            sx={{ mt: 1, fontSize: '0.75rem', textTransform: 'none' }}
            fullWidth
          >
            Show Time Series
          </Button>
        )}
      </CardContent>
    </Card>
  );
});

interface MapViewProps {
  onStationSelect?: (stationUuid: string) => void;
  onAddStation?: (location: { lat: number; lng: number }) => void;
  onScrollToTimeSeries?: () => void;
}

// Component to recenter map
const RecenterMap: React.FC<{ center: [number, number]; triggerCount?: number }> = ({ center, triggerCount }) => {
  const map = useMap();
  useEffect(() => {
    map.setView(center, map.getZoom());
  }, [map, center, triggerCount]);
  return null;
};

// Component to handle map clicks for adding stations
const MapClickHandler: React.FC<{ onAddStation?: (location: { lat: number; lng: number }) => void }> = ({ onAddStation }) => {
  useMapEvents({
    click: (e) => {
      if (onAddStation) {
        const { lat, lng } = e.latlng;
        console.log('Map clicked at:', { lat, lng }); // Debug log
        onAddStation({ lat, lng });
      }
    },
  });
  return null;
};

// Component to handle map clicks for setting radius filter
const FilterClickHandler: React.FC<{
  isActive: boolean;
  onSetFilterCenter: (location: [number, number]) => void;
}> = ({ isActive, onSetFilterCenter }) => {
  useMapEvents({
    click: (e) => {
      if (isActive) {
        const { lat, lng } = e.latlng;
        onSetFilterCenter([lat, lng]);
      }
    },
  });
  return null;
};

const createCustomIcon = (type: 'owner' | 'public' | 'shared' = 'owner') => {
  const colors: Record<string, { fill: string; border: string; hollow: boolean }> = {
    owner:  { fill: '#2196f3', border: 'white',   hollow: false }, // Blue filled
    public: { fill: 'white',   border: '#558b2f', hollow: true  }, // Green ring
    shared: { fill: '#ff9800', border: 'white',   hollow: false }, // Orange filled
  };
  const c = colors[type];

  return new DivIcon({
    html: `
      <div style="
        background-color: ${c.fill};
        width: 25px;
        height: 25px;
        border-radius: 50%;
        border: ${c.hollow ? '4px' : '3px'} solid ${c.border};
        box-shadow: 0 2px 6px rgba(0,0,0,0.3);
        display: flex;
        align-items: center;
        justify-content: center;
        position: relative;
      ">
        <div style="
          position: absolute;
          bottom: -8px;
          left: 50%;
          transform: translateX(-50%);
          width: 0;
          height: 0;
          border-left: 6px solid transparent;
          border-right: 6px solid transparent;
          border-top: 8px solid ${c.hollow ? c.border : c.fill};
        "></div>
      </div>
    `,
    className: 'custom-marker',
    iconSize: [25, 35],
    iconAnchor: [12, 35],
  });
};

const MapView: React.FC<MapViewProps> = ({ onStationSelect, onAddStation, onScrollToTimeSeries }) => {
  const theme = useTheme();
  // Use refs for callbacks to avoid re-rendering markers on callback change
  const onStationSelectRef = React.useRef(onStationSelect);
  onStationSelectRef.current = onStationSelect;
  const onScrollToTimeSeriesRef = React.useRef(onScrollToTimeSeries);
  onScrollToTimeSeriesRef.current = onScrollToTimeSeries;

  const [stations, setStations] = useState<Station[]>([]);
  const [loading, setLoading] = useState(true);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [mapCenter, setMapCenter] = useState<[number, number]>([39.0742, 21.8243]); // Greece
  const [userLocation, setUserLocation] = useState<[number, number] | null>(null);
  const [recenterTrigger, setRecenterTrigger] = useState(0);

  // Radius filter state
  const [radiusFilter, setRadiusFilter] = useState<RadiusFilter | null>(null);
  const [isSettingFilter, setIsSettingFilter] = useState(false);
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const [pendingFilterCenter, setPendingFilterCenter] = useState<[number, number] | null>(null);
  const [radiusInput, setRadiusInput] = useState<string>('10');

  // Theme detection for UI elements
  const isDarkMode = theme.palette.mode === 'dark';

  // GISCO: CartoDB no-labels basemap (avoids label doubling with GISCO toponyms)
  // const tileLayerUrl = "https://{s}.basemaps.cartocdn.com/light_nolabels/{z}/{x}/{y}.png";
  const tileLayerUrl = "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png";

  // GISCO: boundaries and toponyms state
  const [giscoBoundaries, setGiscoBoundaries] = useState<any>(null);
  const [giscoLabelPoints, setGiscoLabelPoints] = useState<any>(null);

  useEffect(() => {
    fetchStations();
    getCurrentLocation();

    // GISCO: Fetch World Boundaries + Label Points
    fetch('https://gisco-services.ec.europa.eu/distribution/v2/countries/geojson/CNTR_RG_20M_2024_4326.geojson')
      .then(res => res.json())
      .then(data => setGiscoBoundaries(data))
      .catch(err => console.warn('Failed to load GISCO boundaries:', err));
    fetch('https://gisco-services.ec.europa.eu/distribution/v2/countries/geojson/CNTR_LB_2024_4326.geojson')
      .then(res => res.json())
      .then(data => setGiscoLabelPoints(data))
      .catch(err => console.warn('Failed to load GISCO label points:', err));

    // Inject custom popup styles
    const styleElement = document.createElement('style');
    styleElement.textContent = popupStyles;
    document.head.appendChild(styleElement);

    return () => {
      // Cleanup styles on unmount
      if (styleElement.parentNode) {
        styleElement.parentNode.removeChild(styleElement);
      }
    };
  }, []);

  // Refetch stations when radius filter changes
  useEffect(() => {
    fetchStations();
  }, [radiusFilter]);

  const fetchStations = async () => {
    try {
      // Only show full loading screen on initial load
      if (isInitialLoad) {
        setLoading(true);
      }
      const data = await mapService.getStations(radiusFilter || undefined);
      setStations(data);

      // Center map on first station if available (only on initial load, not filter changes)
      if (isInitialLoad && data.length > 0) {
        const firstStation = data[0];
        if (firstStation.location?.latitude && firstStation.location?.longitude) {
          setMapCenter([firstStation.location.latitude, firstStation.location.longitude]);
        }
      }
    } catch (error) {
      console.error('Error fetching stations:', error);
    } finally {
      setLoading(false);
      setIsInitialLoad(false);
    }
  };

  // Handle filter dialog confirm
  const handleFilterConfirm = () => {
    if (pendingFilterCenter && radiusInput) {
      const radius = parseFloat(radiusInput);
      if (radius > 0) {
        setRadiusFilter({
          lat: pendingFilterCenter[0],
          lon: pendingFilterCenter[1],
          radius: radius
        });
      }
    }
    setFilterDialogOpen(false);
    setIsSettingFilter(false);
    setPendingFilterCenter(null);
  };

  // Clear the radius filter
  const handleClearFilter = () => {
    setRadiusFilter(null);
    setIsSettingFilter(false);
  };

  const getCurrentLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          setUserLocation([latitude, longitude]);
        },
        (error) => {
          console.log('Geolocation error:', error);
        }
      );
    }
  };

  const handleMyLocation = () => {
    if (userLocation) {
      setMapCenter(userLocation);
      setRecenterTrigger(prev => prev + 1); // Force recenter even if same location
    } else {
      // Request location with user feedback
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            const { latitude, longitude } = position.coords;
            const newLocation: [number, number] = [latitude, longitude];
            setUserLocation(newLocation);
            setMapCenter(newLocation);
            setRecenterTrigger(prev => prev + 1);
          },
          (error) => {
            console.error('Geolocation error:', error);
            alert('Unable to get your location. Please enable location services in your browser.');
          }
        );
      } else {
        alert('Geolocation is not supported by your browser.');
      }
    }
  };

  const giscoBoundaryStyle = {
    color: '#2c3e50',
    weight: 2,
    opacity: 0.8,
    fillColor: 'transparent',
    fillOpacity: 0,
  };

  const toponymLabels = React.useMemo(() => {
    // Use official GISCO label points (pre-computed correct positions)
    if (giscoLabelPoints) {
      return giscoLabelPoints.features
        .filter((f: any) => f.properties?.NAME_ENGL && f.geometry?.coordinates)
        .map((feature: any) => ({
          name: feature.properties.NAME_ENGL,
          lat: feature.geometry.coordinates[1],
          lng: feature.geometry.coordinates[0],
        }));
    }
    return [];
  }, [giscoLabelPoints]);

  const [showGiscoBoundaries, setShowGiscoBoundaries] = useState(true);
  const [showGiscoToponyms, setShowGiscoToponyms] = useState(false);

  const [showOsm, setShowOsm] = useState(true);

  const GiscoLayerEvents = () => {
    const map = useMap();
    useEffect(() => {
      const updateBaseOpacity = () => {
        map.eachLayer((l: any) => {
          if (l.setOpacity && l._url && l._url.includes('openstreetmap')) {
            const giscoActive = showGiscoBoundaries || showGiscoToponyms;
            l.setOpacity(giscoActive ? 0.5 : 1);
          }
        });
      };
      const onAdd = (e: any) => {
        if (e.name === 'GISCO Boundaries') setShowGiscoBoundaries(true);
        if (e.name === 'Official EC Toponyms') setShowGiscoToponyms(true);
        if (e.name === 'OpenStreetMap') setShowOsm(true);
      };
      const onRemove = (e: any) => {
        if (e.name === 'GISCO Boundaries') setShowGiscoBoundaries(false);
        if (e.name === 'Official EC Toponyms') setShowGiscoToponyms(false);
        if (e.name === 'OpenStreetMap') setShowOsm(false);
      };
      map.on('overlayadd', onAdd);
      map.on('overlayremove', onRemove);
      return () => { map.off('overlayadd', onAdd); map.off('overlayremove', onRemove); };
    }, [map]);
    return null;
  };

  // Manage base layer opacity based on GISCO state
  const GiscoBaseOpacity = () => {
    const map = useMap();
    useEffect(() => {
      const giscoActive = showGiscoBoundaries || showGiscoToponyms;
      map.eachLayer((l: any) => {
        if (l.setOpacity && l._url && (l._url.includes('openstreetmap') || l._url.includes('cartocdn'))) {
          l.setOpacity(giscoActive ? 0.5 : 1);
        }
      });
    }, [showGiscoBoundaries, showGiscoToponyms, map]);
    return null;
  };

  const GiscoDisclaimer = () => {
    const map = useMap();
    useEffect(() => {
      const disclaimer = new L.Control({ position: 'bottomright' });
      disclaimer.onAdd = () => {
        const div = L.DomUtil.create('div', 'gisco-disclaimer');
        div.style.cssText = 'background:rgba(255,255,255,0.85);padding:4px 8px;font-size:10px;max-width:420px;line-height:1.3;color:#333;border-radius:3px;pointer-events:none;margin-right:10px;margin-bottom:5px;';
        div.innerHTML = 'The designations employed and the presentation of material on this map do not imply the expression of any opinion whatsoever concerning the legal status of any country, territory, city or area or of its authorities, or concerning the delimitation of its frontiers or boundaries.';
        return div;
      };
      disclaimer.addTo(map);
      return () => { disclaimer.remove(); };
    }, [map]);
    return null;
  };

  // Only show loading screen on initial load, not during filter refetch
  if (loading && isInitialLoad) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <Typography>Loading map...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ position: 'relative', height: '600px', borderRadius: 2, overflow: 'visible' }}>
      <MapContainer
        center={mapCenter}
        zoom={6}
        style={{ height: '100%', width: '100%' }}
        attributionControl={false}
      >
        <GiscoLayerEvents />
        <GiscoBaseOpacity />
        <LayersControl position="topright">
          <LayersControl.Overlay checked name="OpenStreetMap">
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              url={tileLayerUrl}
              subdomains="abc"
              maxZoom={19}
            />
          </LayersControl.Overlay>
          <LayersControl.Overlay checked name="GISCO Boundaries">
            <LayerGroup>
              {giscoBoundaries && (
                <GeoJSON data={giscoBoundaries} style={giscoBoundaryStyle} />
              )}
            </LayerGroup>
          </LayersControl.Overlay>
          <LayersControl.Overlay name="Official EC Toponyms">
            <LayerGroup>
              {toponymLabels.map((t: any) => (
                <Marker key={t.name} position={[t.lat, t.lng]} interactive={false}
                  icon={new DivIcon({
                    className: 'gisco-toponym',
                    html: `<span style="font-size:14px;font-weight:700;color:#1a237e;text-shadow:-1px -1px 0 #fff,1px -1px 0 #fff,-1px 1px 0 #fff,1px 1px 0 #fff,0 -1px 0 #fff,0 1px 0 #fff,-1px 0 0 #fff,1px 0 0 #fff;white-space:nowrap;pointer-events:none;">${t.name}</span>`,
                    iconSize: [0, 0], iconAnchor: [0, 0],
                  })}
                />
              ))}
            </LayerGroup>
          </LayersControl.Overlay>
        </LayersControl>
        <GiscoDisclaimer />

        <RecenterMap center={mapCenter} triggerCount={recenterTrigger} />

        {/* User location marker */}
        {userLocation && (
          <Marker 
            position={userLocation}
            icon={new DivIcon({
              html: `
                <div style="
                  background-color: #2196f3;
                  width: 15px;
                  height: 15px;
                  border-radius: 50%;
                  border: 3px solid white;
                  box-shadow: 0 0 10px rgba(33, 150, 243, 0.8);
                "></div>
              `,
              iconSize: [15, 15],
              iconAnchor: [8, 8],
            })}
          >
            <Popup>
              <Typography variant="body2">
                <strong>Your Location</strong>
              </Typography>
            </Popup>
          </Marker>
        )}

        {/* Station markers with clustering */}
        <MarkerClusterGroup
          chunkedLoading
          maxClusterRadius={50}
          spiderfyOnMaxZoom
          showCoverageOnHover={false}
          iconCreateFunction={(cluster: any) => {
            const count = cluster.getChildCount();
            let size = 'small';
            let dim = 36;
            if (count >= 100) { size = 'large'; dim = 50; }
            else if (count >= 10) { size = 'medium'; dim = 42; }
            return new DivIcon({
              html: `<div style="
                background: rgba(0, 212, 255, 0.85);
                color: #fff;
                border-radius: 50%;
                width: ${dim}px;
                height: ${dim}px;
                display: flex;
                align-items: center;
                justify-content: center;
                font-weight: 700;
                font-size: ${count >= 100 ? '14px' : '13px'};
                border: 3px solid rgba(255,255,255,0.8);
                box-shadow: 0 2px 8px rgba(0,0,0,0.3);
              ">${count}</div>`,
              className: 'marker-cluster-custom',
              iconSize: [dim, dim],
            });
          }}
        >
          {stations.filter(station =>
            station.location?.latitude &&
            station.location?.longitude &&
            station.status !== 'debug' &&
            (station.is_owner || (station as any).is_shared || station.status === 'active')
          ).map((station) => (
            <Marker
              key={station.uuid || station.id}
              position={[station.location?.latitude || 0, station.location?.longitude || 0]}
              icon={createCustomIcon(station.is_owner ? 'owner' : (station as any).is_shared ? 'shared' : 'public')}
              eventHandlers={{
                click: () => {
                  // Auto-open details/charts below map (use ref to avoid re-render)
                  onStationSelectRef.current && onStationSelectRef.current((station.uuid || station.id) as string);
                }
              }}
            >
              <Popup maxWidth={400} minWidth={320} autoPan={false}>
                <StationPopupContent
                  station={station}
                  isDarkMode={isDarkMode}
                  onViewDetails={() => {}}
                  onShowTimeSeries={() => onScrollToTimeSeriesRef.current?.()}
                />
              </Popup>
            </Marker>
          ))}
        </MarkerClusterGroup>

        {/* Radius filter click handler */}
        <FilterClickHandler
          isActive={isSettingFilter}
          onSetFilterCenter={(center) => {
            setPendingFilterCenter(center);
            setFilterDialogOpen(true);
          }}
        />

        {/* Radius filter circle */}
        {radiusFilter && (
          <Circle
            center={[radiusFilter.lat, radiusFilter.lon]}
            radius={radiusFilter.radius * 1000}
            pathOptions={{
              color: '#558b2f',
              fillColor: '#558b2f',
              fillOpacity: 0.1,
              weight: 2
            }}
          />
        )}

        {/* Radius filter center marker */}
        {radiusFilter && (
          <Marker
            position={[radiusFilter.lat, radiusFilter.lon]}
            icon={new DivIcon({
              html: `
                <div style="
                  background-color: #558b2f;
                  width: 12px;
                  height: 12px;
                  border-radius: 50%;
                  border: 2px solid white;
                  box-shadow: 0 2px 6px rgba(0,0,0,0.3);
                "></div>
              `,
              className: '',
              iconSize: [12, 12],
              iconAnchor: [6, 6],
            })}
          />
        )}
      </MapContainer>

      {/* Floating Action Buttons */}
      <Box sx={{ position: 'absolute', bottom: 16, left: '50%', transform: 'translateX(-50%)', display: 'flex', flexDirection: 'row', gap: 1, zIndex: 1000 }}>
        <Tooltip title="My Location" placement="left">
          <Fab size="small" color="primary" onClick={handleMyLocation}>
            <MyLocation />
          </Fab>
        </Tooltip>

        {/* Radius filter buttons */}
        {!radiusFilter && !isSettingFilter && (
          <Tooltip title="Set radius filter - click to place center" placement="left">
            <Fab size="small" sx={{ bgcolor: '#558b2f', '&:hover': { bgcolor: '#33691e' } }} onClick={() => setIsSettingFilter(true)}>
              <FilterAlt sx={{ color: 'white' }} />
            </Fab>
          </Tooltip>
        )}

        {isSettingFilter && !radiusFilter && (
          <Tooltip title="Cancel filter" placement="left">
            <Fab size="small" color="default" onClick={() => setIsSettingFilter(false)}>
              <Clear />
            </Fab>
          </Tooltip>
        )}

        {radiusFilter && (
          <Tooltip title="Clear radius filter" placement="left">
            <Fab size="small" sx={{ bgcolor: '#558b2f', '&:hover': { bgcolor: '#33691e' } }} onClick={handleClearFilter}>
              <Clear sx={{ color: 'white' }} />
            </Fab>
          </Tooltip>
        )}
      </Box>

      {/* Filter mode indicator */}
      {isSettingFilter && !filterDialogOpen && (
        <Box sx={{
          position: 'absolute',
          top: 16,
          left: '50%',
          transform: 'translateX(-50%)',
          bgcolor: '#558b2f',
          color: 'white',
          px: 3,
          py: 1,
          borderRadius: 2,
          boxShadow: 3,
          zIndex: 1000
        }}>
          <Typography variant="body2">
            Click on the map to set filter center
          </Typography>
        </Box>
      )}

      {/* Map Legend */}
      <Box sx={{
        position: 'absolute',
        bottom: 16,
        left: 16,
        bgcolor: 'background.paper',
        p: 2,
        borderRadius: 1,
        boxShadow: 2,
        minWidth: 150,
        zIndex: 1000
      }}>
        <Typography variant="subtitle2" gutterBottom>
          Stations
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
          <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#2196f3', mr: 1 }} />
          <Typography variant="caption">My Stations</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
          <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#ff9800', mr: 1 }} />
          <Typography variant="caption">Shared with me</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
          <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: 'white', border: '3px solid #558b2f', mr: 1, boxSizing: 'border-box' }} />
          <Typography variant="caption">Public</Typography>
        </Box>
      </Box>

      {/* Radius filter info */}
      {radiusFilter && (
        <Box sx={{
          position: 'absolute',
          top: 16,
          left: 16,
          bgcolor: 'background.paper',
          p: 1.5,
          borderRadius: 1,
          boxShadow: 2,
          zIndex: 1000
        }}>
          <Typography variant="caption" color="text.secondary">
            Radius Filter: {radiusFilter.radius} km
          </Typography>
          <Typography variant="caption" display="block" color="text.secondary">
            Stations: {stations.length}
          </Typography>
        </Box>
      )}

      {/* Radius input dialog */}
      <Dialog open={filterDialogOpen} onClose={() => { setFilterDialogOpen(false); setIsSettingFilter(false); }}>
        <DialogTitle>Set Radius Filter</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Enter the radius in kilometers to filter stations around the selected point.
          </Typography>
          <TextField
            autoFocus
            label="Radius (km)"
            type="number"
            fullWidth
            value={radiusInput}
            onChange={(e) => setRadiusInput(e.target.value)}
            inputProps={{ min: 1, max: 1000, step: 1 }}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setFilterDialogOpen(false); setIsSettingFilter(false); }}>
            Cancel
          </Button>
          <Button onClick={handleFilterConfirm} variant="contained" color="primary">
            Apply Filter
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default React.memo(MapView);